import { useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box, Paper, Typography, TextField, Button, Grid,
} from '@mui/material';
import StorefrontIcon from '@mui/icons-material/Storefront';
import { api } from '../../api/api';
import toast from 'react-hot-toast';

const formatPhoneDisplay = (digits) => {
  const d = (digits || '').replace(/\D/g, '').slice(0, 11);
  if (d.length <= 3) return d;
  if (d.length <= 6) return `(${d.slice(0, 3)}) ${d.slice(3)}`;
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6, 10)}`;
};

const emptyForm = {
  tljNo: '', name: '',
  zipcode: '', address1: '', address2: '', town: '', state: '',
  storePhone: '',
  ownerName: '', ownerPhone: '', ownerEmail: '',
  managerName: '', managerPhone: '', managerEmail: '',
  screenCount: '', status: 'active',
};

function StoreForm() {
  const { token } = useParams();
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'storePhone') {
      setForm(f => ({ ...f, [name]: (value || '').replace(/\D/g, '').slice(0, 11) }));
      return;
    }
    setForm(f => ({ ...f, [name]: value }));
  };

  const handleZipcodeBlur = async () => {
    if (!/^\d{5}$/.test(form.zipcode)) return;
    try {
      const res = await api.get(`/zipcode/${form.zipcode}`);
      if (res.data) {
        const d = res.data;
        setForm(f => ({ ...f, town: d.city || f.town, state: d.stateAbbr || d.state || f.state }));
      }
    } catch (e) {}
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!token) {
      toast.error('유효하지 않은 링크입니다.');
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form, token, screenCount: Number(form.screenCount) || 0 };
      const res = await api.post('/store/customer-form', payload);
      if (res.status === 201 || res.status === 200) {
        setSaved(true);
        toast.success('저장되었습니다.');
      } else {
        toast.error(res.data?.message || '저장에 실패했습니다.');
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || '저장에 실패했습니다.');
    } finally {
      setSaving(false);
    }
  };

  if (saved) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2, background: 'linear-gradient(135deg, #f5f3ff 0%, #ede9fe 100%)' }}>
        <Paper sx={{ p: 4, maxWidth: 400, textAlign: 'center', borderRadius: 3 }}>
          <StorefrontIcon sx={{ color: '#551FFF', fontSize: 48, mb: 2 }} />
          <Typography variant="h6" sx={{ fontWeight: 700, color: '#551FFF', mb: 1 }}>
            점포 정보가 저장되었습니다.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            관리자 확인 후 연락드리겠습니다.
          </Typography>
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', p: 2, background: 'linear-gradient(135deg, #f5f3ff 0%, #ede9fe 100%)' }}>
      <Paper sx={{ maxWidth: 640, mx: 'auto', p: 4, borderRadius: 3, boxShadow: '0 4px 20px rgba(85,31,255,0.12)' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
          <StorefrontIcon sx={{ color: '#551FFF', fontSize: 28 }} />
          <Typography variant="h5" sx={{ fontWeight: 700, color: '#551FFF' }}>
            점포 정보 입력
          </Typography>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          링크를 통해 전달받으신 경우 아래 폼을 작성 후 저장해 주세요.
        </Typography>

        <form onSubmit={handleSubmit}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth size="small" label="TLJ No" name="tljNo" value={form.tljNo} onChange={handleChange} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth size="small" label="점포명" name="name" value={form.name} onChange={handleChange} />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ color: '#551FFF', mb: 0.5 }}>주소 (집코드 입력 후 blur 시 City, State 자동 입력)</Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth size="small" label="Zip Code" name="zipcode" value={form.zipcode} onChange={handleChange} onBlur={handleZipcodeBlur} placeholder="12345" />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth size="small" label="Address 1" name="address1" value={form.address1} onChange={handleChange} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth size="small" label="Address 2" name="address2" value={form.address2} onChange={handleChange} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth size="small" label="City" name="town" value={form.town} onChange={handleChange} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth size="small" label="State" name="state" value={form.state} onChange={handleChange} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth size="small" label="매장 전화" name="storePhone"
                value={formatPhoneDisplay(form.storePhone)}
                onChange={(e) => setForm(f => ({ ...f, storePhone: (e.target.value || '').replace(/\D/g, '').slice(0, 11) }))}
                placeholder="(000) 000-0000"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth size="small" label="화면 수" name="screenCount" type="number" value={form.screenCount} onChange={handleChange} />
            </Grid>
            <Grid item xs={12}><Typography variant="subtitle2" sx={{ color: '#551FFF', mt: 1 }}>대표 정보</Typography></Grid>
            <Grid item xs={12} sm={4}>
              <TextField fullWidth size="small" label="대표 이름" name="ownerName" value={form.ownerName} onChange={handleChange} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField fullWidth size="small" label="대표 전화" name="ownerPhone" value={form.ownerPhone} onChange={handleChange} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField fullWidth size="small" label="대표 이메일" name="ownerEmail" value={form.ownerEmail} onChange={handleChange} />
            </Grid>
            <Grid item xs={12}><Typography variant="subtitle2" sx={{ color: '#551FFF', mt: 1 }}>점장 정보</Typography></Grid>
            <Grid item xs={12} sm={4}>
              <TextField fullWidth size="small" label="점장 이름" name="managerName" value={form.managerName} onChange={handleChange} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField fullWidth size="small" label="점장 전화" name="managerPhone" value={form.managerPhone} onChange={handleChange} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField fullWidth size="small" label="점장 이메일" name="managerEmail" value={form.managerEmail} onChange={handleChange} />
            </Grid>
          </Grid>
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 4 }}>
            <Button type="submit" variant="contained" disabled={saving} sx={{ background: '#551FFF', borderRadius: 2 }}>
              {saving ? '저장 중...' : '저장'}
            </Button>
          </Box>
        </form>
      </Paper>
    </Box>
  );
}

export default StoreForm;
